import { createSelector } from 'reselect';
import { constUndefined, flow } from 'fp-ts/function';
import { option, array } from 'fp-ts';
import { composeProps, createPropsExtender, ifProps, Selector } from '@mews/framework';
import { ApplicationState } from '@mews-commander/types';
import { ResolveRoute } from '@mews/routing';
import { getOrFalse, getProp, getStringProp } from '@mews/fp';
import { RouteName } from '@mews-commander/enums';
import { AccountingEnvironment, GeneralEnterprise } from '@mews-commander/data-access';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { getLegalEnvironmentCountryCode as getLegalEnvironmentCountryCodeJS } from '../ducks/legalEnvironments';
import {
    getEnterpriseAccountingEnvironmentCode,
    getEnterpriseLegalEnvironmentCode,
    getEnterprisesByIds,
} from '../ducks/enterprises';
import { getAccountingEnvironmentByCode } from '../ducks/accountingEnvironments';
import { getTargetEnterpriseIdsBySourceId } from '../ducks/enterpriseRelationships';

const getLegalEnvironmentCountryCode = getLegalEnvironmentCountryCodeJS as Selector<
    ApplicationState,
    { legalEnvironmentCode: string },
    string | undefined
>;

export const getEnterpriseCountryCode = composeProps(
    ifProps(['legalEnvironmentCode'], getLegalEnvironmentCountryCode),
    createPropsExtender('legalEnvironmentCode', getEnterpriseLegalEnvironmentCode)
);

const getEnterpriseAccountingEnvironment = composeProps(
    getAccountingEnvironmentByCode,
    createPropsExtender('code', getEnterpriseAccountingEnvironmentCode)
);

export const canEnterpriseAccountingEnvironmentUseBillingUnits = createSelector(
    getEnterpriseAccountingEnvironment,
    getOrFalse('useBillingUnits')
);

export const canEnterpriseUseAllowCustomerBillAddressObjection = createSelector(
    getEnterpriseAccountingEnvironment,
    (accountingEnvironment: AccountingEnvironment) => !!accountingEnvironment?.allowCustomerBillAddressObjection
);

export const getEnterpriseDetailUrl = createSelector(
    getProp<ResolveRoute>()('resolveRoute'),
    getStringProp('enterpriseId'),
    (resolveRoute, enterpriseId) => resolveRoute(RouteName.EnterpriseDetail, { enterpriseId })
);

export const getRelatedTargetEnterprises = composeProps(
    ifProps(['ids'], getEnterprisesByIds, [] as ReturnType<typeof getEnterprisesByIds>),
    createPropsExtender('ids', flow(getTargetEnterpriseIdsBySourceId, option.getOrElseW(constUndefined)))
);

export const getCurrentEnterpriseRelatedTargetEnterprises = composeProps(
    getRelatedTargetEnterprises,
    createPropsExtender('sourceEnterpriseId', getCurrentEnterpriseId)
) as unknown as <S>(state: S) => Array<GeneralEnterprise>;

export const getCurrentEnterpriseRelatedTargetEnterprisesId = createSelector(
    getCurrentEnterpriseRelatedTargetEnterprises,
    array.map(({ id }) => id)
);
